import { useMemo } from "react";
import { useUserSettingsContext } from "@/contexts/UserSettingsContext";
import { useFeatureGate } from "@/hooks/useFeatureGate";
import { usePaymentHistory } from "@/hooks/usePaymentHistory";
import { type DatabaseSubscription } from "@/hooks/useSubscriptions";
import { deriveNextPaymentDate, daysUntil } from "@/lib/billingSchedule";
import { filterIncludedForFreeTier } from "@/lib/currencyGate";
import { toISODateString } from "@/lib/dateUtils";
import { getPendingCount } from "@/lib/pendingLogic";
import { type ViewingPeriod } from "@/lib/periodStorageUtils";
import { calculatePeriodSpend } from "@/lib/totals";

export const useDashboardSummary = (
  subscriptions: DatabaseSubscription[],
  currentPeriod: ViewingPeriod,
  includePaygInStats: boolean = false,
) => {
  const { displayCurrency } = useUserSettingsContext();
  const { canConvertCurrency, isLoading: isFeatureGateLoading } =
    useFeatureGate();
  const { payments } = usePaymentHistory();

  const calculations = useMemo(() => {
    // All active subscriptions (include PAYG in counts)
    const allActive = subscriptions.filter((sub) => sub.status === "active");
    const recurringActive = allActive.filter(
      (sub) => (sub.billing_type || "recurring") !== "pay_as_you_go",
    );
    const canUseCurrencyConversion = isFeatureGateLoading || canConvertCurrency;
    const { primaryCurrency, included, excluded } = filterIncludedForFreeTier(
      allActive,
      canUseCurrencyConversion,
    );
    // Recurring subscriptions only for predictable-spend calculations.
    const subscriptionsForTotals = includePaygInStats
      ? included
      : included.filter(
          (sub) => (sub.billing_type || "recurring") !== "pay_as_you_go",
        );
    const totalCurrency = canUseCurrencyConversion
      ? displayCurrency
      : (primaryCurrency ?? displayCurrency);

    // Calculate actual monthly spend for selected period (date-aware)
    const monthPeriodStart = new Date(
      currentPeriod.year,
      currentPeriod.month,
      1,
    );
    const monthPeriodEnd = new Date(
      currentPeriod.year,
      currentPeriod.month + 1,
      0,
      23,
      59,
      59,
    );

    const { periodTotal: monthlyTotal } = calculatePeriodSpend(
      subscriptionsForTotals,
      monthPeriodStart,
      monthPeriodEnd,
      {
        includePayg: includePaygInStats,
        excludeCancelled: true,
        displayCurrency: totalCurrency,
      },
      payments,
    );

    // Calculate actual yearly spend for selected year (date-aware)
    const yearPeriodStart = new Date(currentPeriod.year, 0, 1); // Jan 1
    const yearPeriodEnd = new Date(currentPeriod.year, 11, 31, 23, 59, 59); // Dec 31

    const { periodTotal: yearlyTotal } = calculatePeriodSpend(
      subscriptionsForTotals,
      yearPeriodStart,
      yearPeriodEnd,
      {
        includePayg: includePaygInStats,
        excludeCancelled: true,
        displayCurrency: totalCurrency,
      },
      payments,
    );

    // Get next payment (derived from recurring subscriptions only)
    const nextPayment =
      recurringActive.length > 0
        ? (() => {
            const withDerivedDates = recurringActive
              .map((sub) => {
                const derivedNext = deriveNextPaymentDate({
                  billingCycle: sub.billing_cycle,
                  storedNextPaymentDate: sub.next_payment_date,
                  paidDates: payments
                    .filter((payment) => payment.subscription_id === sub.id)
                    .filter((payment) => payment.status === "paid")
                    .map((payment) => payment.payment_date),
                });
                if (!derivedNext) return null;

                const nextPaymentDate = toISODateString(derivedNext);
                if (!nextPaymentDate) return null;

                if (
                  sub.cancellation_date &&
                  derivedNext > new Date(sub.cancellation_date)
                ) {
                  return null;
                }

                return {
                  subscription: {
                    ...sub,
                    next_payment_date: nextPaymentDate,
                  },
                  daysUntil: daysUntil(derivedNext),
                };
              })
              .filter(
                (
                  item,
                ): item is {
                  subscription: DatabaseSubscription & {
                    next_payment_date: string;
                  };
                  daysUntil: number;
                } => item !== null,
              )
              .sort(
                (a, b) =>
                  new Date(a.subscription.next_payment_date!).getTime() -
                  new Date(b.subscription.next_payment_date!).getTime(),
              );

            return withDerivedDates[0] ?? null;
          })()
        : null;

    const activeCount = allActive.length;
    const pendingCount = getPendingCount(subscriptions, payments);
    const paygActiveCount = allActive.filter(
      (s) => (s.billing_type || "recurring") === "pay_as_you_go",
    ).length;

    return {
      monthlyTotal,
      yearlyTotal,
      nextPayment,
      activeCount,
      pendingCount,
      paygActiveCount,
      excludedCurrencyCount: excluded.length,
      primaryCurrency,
      totalCurrency,
    };
  }, [
    subscriptions,
    canConvertCurrency,
    displayCurrency,
    currentPeriod,
    isFeatureGateLoading,
    payments,
    includePaygInStats,
  ]);

  return calculations;
};
